import axios from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';

/**
 * 전역 상태 (모든 useAxiosAdminFetch 인스턴스가 공유)
 * - 리프레시 중복 호출 방지
 * - Swal 중복 표시 방지
 */
let isRefreshing = false;
let refreshPromise = null;
let hasShownLoginAlert = false; // Swal 중복 표시 방지

const useAxiosAdminFetch = () => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { storeNo } = useParams();

  const paramsRef = useRef({ storeNo });
  const navigateRef = useRef(navigate);
  const locationRef = useRef(location);

  useEffect(() => {
    paramsRef.current = { storeNo };
    navigateRef.current = navigate;
    locationRef.current = location;
  }, [storeNo, navigate, location]);

  // 페이지 이동 시 Swal 플래그 리셋
  useEffect(() => {
    hasShownLoginAlert = false;
  }, [location.pathname]);

  /**
   * 로그인 페이지로 이동 (중복 호출 방지)
   * - 리프레시 토큰 만료, 또는 상점 비활성화(403) 시 호출됨
   */
  const goToLoginPage = useCallback((reason) => {
    // 이미 로그인 알림을 보여줬으면 중복 실행 방지
    if (hasShownLoginAlert) {
      return;
    }
    hasShownLoginAlert = true;

    const navigateFn = navigateRef.current;
    const currentLocation = locationRef.current;

    // 현재 경로가 이미 로그인 페이지면 무시
    if (currentLocation.pathname.includes('/login')) {
      hasShownLoginAlert = false;
      return;
    }

    /**
     * 경로에서 storeNo 추출
     * 예: /mobile/giftstatus/1 → 1
     *     /mobile/verifyconfirm/1/abc123/5 → 1
     */
    const extractStoreNoFromPath = (pathname) => {
      // URL 파라미터에서 먼저 확인
      const { storeNo: paramStoreNo } = paramsRef.current;
      if (paramStoreNo) {
        return paramStoreNo;
      }

      // 경로에서 추출
      const segments = pathname.split('/').filter(Boolean);
      
      // /mobile/giftstatus/1 → ['mobile', 'giftstatus', '1']
      // /mobile/verifyconfirm/1/abc123/5 → ['mobile', 'verifyconfirm', '1', 'abc123', '5']
      const mobileIndex = segments.indexOf('mobile');
      if (mobileIndex !== -1) {
        // mobile 다음 경로명(giftstatus, verifyconfirm 등) 다음이 storeNo
        // mobile 다음이 경로명이고, 그 다음이 storeNo
        if (segments[mobileIndex + 2]) {
          const possibleStoreNo = segments[mobileIndex + 2];
          // 숫자인지 확인
          if (/^\d+$/.test(possibleStoreNo)) {
            return possibleStoreNo;
          }
        }
      }

      // /admin/login/1 같은 경우
      const loginIndex = segments.indexOf('login');
      if (loginIndex !== -1 && segments[loginIndex + 1]) {
        const possibleStoreNo = segments[loginIndex + 1];
        if (/^\d+$/.test(possibleStoreNo)) {
          return possibleStoreNo;
        }
      }

      // localStorage에서 마지막 storeNo 확인
      const lastStoreNo = localStorage.getItem('last_storeNo');
      if (lastStoreNo) {
        return lastStoreNo;
      }

      return null;
    };

    // storeNo 추출 (경로에서 또는 params에서)
    const storeNo = extractStoreNoFromPath(currentLocation.pathname);

    const alertText =
      reason === 403
        ? '영업중지된 상점입니다. 상점지원센터에 문의해주세요(070-4010-8203).'
        : '세션이 만료되었습니다. 다시 로그인해주세요.';

    // 정산명세서 공통 링크(/mobile/settlement, /mobile/settlement/{정산월})는 주소에 storeNo가 없어 상점 공통 로그인으로 복귀 경로를 넘긴다.
    const { pathname: currentPathname } = currentLocation;
    // if (/^\/mobile\/settlement(\/[^/]+)?\/?$/.test(currentPathname)) {
    // [2026-11-04] 쿠폰 검증(/mobile/couponverify{confirm|success|failure}/{쿠폰번호})도 주소에 storeNo가 없다.
    //  위 extractStoreNoFromPath 가 쿠폰번호를 storeNo 로 읽어 엉뚱한 상점("○○ 기프트샵") 로그인으로 보냈다
    //  → 상점 공통 로그인 후 같은 검증 화면으로 돌아온다(검증은 로그인 계정의 상점으로 처리).
    if (
      /^\/mobile\/settlement(\/[^/]+)?\/?$/.test(currentPathname) ||
      /^\/mobile\/couponverify(confirm|success|failure)\/\d+\/?$/.test(currentPathname) ||
      // [2026-11-04] 운영자·채널 쿠폰 QR 진입에서 로그인 상점을 읽다가 만료 — 상점 공통 로그인 후 QR 다시 처리
      /^\/app\/qr\/coupon\/[^/]+\/?$/.test(currentPathname)
    ) {
      Swal.fire({
        icon: 'warning',
        title: '로그인 필요',
        text: alertText,
        confirmButtonText: '확인',
      }).then(() => {
        navigateFn(`/mobile/login?redirect=${encodeURIComponent(currentPathname)}`);
      });
      return;
    }

    if (!storeNo) {
      console.warn('[useAxiosAdminFetch] storeNo를 찾을 수 없습니다. 기본 경로로 이동합니다.');
      Swal.fire({
        icon: 'warning',
        title: '로그인 필요',
        text: alertText,
        confirmButtonText: '확인',
      }).then(() => {
        navigateFn('/mobile/login');
      });
      return;
    }

    const currentPath = currentLocation.pathname + currentLocation.search;
    const redirectPath = encodeURIComponent(currentPath);

    Swal.fire({
      icon: 'warning',
      title: '로그인 필요',
      text: alertText,
      confirmButtonText: '확인',
    }).then(() => {
      navigateFn(`/mobile/login/${storeNo}?redirect=${redirectPath}`);
    });
  }, []);

  /**
   * JWT 리프레시 시도 (중복 호출 방지)
   * 
   * 동작:
   * - 리프레시 성공: true 반환 → 페이지 그대로 유지
   * - 리프레시 실패 (401): false 반환 → 로그인 페이지로 이동
   * - 이미 리프레시 중: 진행 중인 Promise 반환 (중복 호출 방지)
   */
  const refetchJwt = useCallback(async () => {
    // 이미 리프레시 중이면 진행 중인 Promise 반환
    if (isRefreshing && refreshPromise) {
      console.log('[useAxiosAdminFetch] 리프레시 이미 진행 중, 대기...');
      return refreshPromise;
    }

    console.log('[useAxiosAdminFetch] 리프레시 시작');
    isRefreshing = true;

    refreshPromise = (async () => {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API_SERVER}refresh`,
          {},
          { withCredentials: true }
        );
        
        console.log('[useAxiosAdminFetch] 리프레시 성공!', response.status);
        // 리프레시 성공 → 페이지 그대로 유지 (로그인 페이지 절대 안 감!)
        return true;
      } catch (e) {
        console.log('[useAxiosAdminFetch] 리프레시 실패:', e.response?.status);

        const status = e.response?.status;
        // 401: 리프레시 토큰 만료. 403: 상점이 운영자에 의해 비활성화됨. 둘 다 재로그인 필요.
        if (status === 401 || status === 403) {
          goToLoginPage(status);
        }
        return false;
      } finally {
        isRefreshing = false;
        refreshPromise = null;
        console.log('[useAxiosAdminFetch] 리프레시 완료, 상태 초기화');
      }
    })();

    return refreshPromise;
  }, [goToLoginPage]);

  const fetchDataRef = useRef(null);

  /**
   * API 요청 실행
   * 
   * 동작 방식:
   * 1. API 요청 → 401 에러 발생
   * 2. 리프레시 토큰으로 액세스 토큰 재발급 시도
   * 3. 리프레시 성공 → 원래 API 재요청 (페이지 그대로 유지!)
   * 4. 리프레시 실패 (401) → 로그인 페이지로 이동
   * 
   * 중요: 리프레시 성공하면 절대 로그인 페이지로 가지 않음!
   */
  const fetchDataImpl = useCallback(
    async (configParams, isRetry = false) => {
      const axiosInstance = axios.create({
        baseURL: process.env.REACT_APP_API_SERVER,
        withCredentials: true,
      });

      setIsLoading(true);
      try {
        const response = await axiosInstance(configParams);
        return response;
      } catch (error) {
        const status = error?.response?.status || error?.request?.status;
        
        if (status === 401) {
          console.log(`[useAxiosAdminFetch] 401 에러 발생, isRetry=${isRetry}`);
          
          if (!isRetry) {
            // 첫 번째 401: 리프레시 시도
            const refreshed = await refetchJwt();
            
            if (refreshed) {
              // 리프레시 성공 → 원래 API 재요청 (페이지 그대로 유지!)
              console.log('[useAxiosAdminFetch] 리프레시 성공, API 재요청');
              return await fetchDataRef.current(configParams, true);
            } else {
              // 리프레시 실패 → goToLoginPage에서 이미 처리됨
              console.log('[useAxiosAdminFetch] 리프레시 실패, 로그인 페이지로 이동됨');
              return error.response;
            }
          }
          
          // 재시도에서도 401 (거의 발생 안 함)
          console.log('[useAxiosAdminFetch] 재시도에서도 401, 로그인 페이지로 이동');
          goToLoginPage();
          return error.response;
        }
        
        return error.response;
      } finally {
        setIsLoading(false);
      }
    },
    [refetchJwt, goToLoginPage]
  );

  fetchDataRef.current = fetchDataImpl;

  const fetchData = useCallback(async (configParams, isRetry = false) => {
    return fetchDataRef.current(configParams, isRetry);
  }, []);

  return { isLoading, fetchData };
};

export default useAxiosAdminFetch;
